import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectTheme } from "../../Store/features/design/designSlice";
import {
	getFilteredInvestorsList,
	createTemplate,
	getTemplates,
	updateTemplate,
	deleteTemplate,
	createFilteredInvestorsList,
	updateFilteredInvestorsList,
	deleteFilteredInvestorsList,
	getCampaigns,
	getCampaignById,
	createCampaign,
	deleteCampaign,
	scheduleCampaign,
	runCampaign
} from "../../Service/user";
import { toast } from "react-toastify";
import { FiEdit, FiEye, FiPlay, FiClock, FiX } from "react-icons/fi";
import { FaRegTrashCan } from "react-icons/fa6";
import { IoCopyOutline } from "react-icons/io5";
import { MdOutlineMail } from "react-icons/md";
import CreateTemplatePopup from "./CreateTemplatePopup/CreateTemplatePopup";
import CreateListPopup from "./CreateListPopup/CreateListPopup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Modal from "react-modal";
import "./Campaign.scss";
import SubscriptionPopup from "../PopUp/SubscriptionPopUp/SubcriptionPop";

const CampaignManagement = () => {
	const theme = useSelector(selectTheme);
	const user = JSON.parse(localStorage.getItem("loggedInUser"));
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState("List");
	const [openCreateTemplate, setOpenCreateTemplate] = useState(false);
	const [templateToEdit, setTemplateToEdit] = useState(null);
	const [lists, setLists] = useState([]);
	const [selectedLists, setSelectedLists] = useState([]);
	const [openCreateList, setOpenCreateList] = useState(false);
	const [listToEdit, setListToEdit] = useState(null);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [listToDelete, setListToDelete] = useState(null);
	const [selectedTemplate, setSelectedTemplate] = useState(null);

	const [templates, setTemplates] = useState([]);

	const [campaigns, setCampaigns] = useState([]);

	const [showScheduleModal, setShowScheduleModal] = useState(false);
	const [scheduledDate, setScheduledDate] = useState(new Date());
	const [scheduledTime, setScheduledTime] = useState(new Date());
	const [isScheduling, setIsScheduling] = useState(false);
	const [campaignToSchedule, setCampaignToSchedule] = useState(null);

	const [subscriptionPopup, setSubscriptionPopup] = useState(false);

	const [loading, setLoading] = useState(false);

	const fetchFilteredInvestorsList = async () => {
		try {
			const response = await getFilteredInvestorsList();
			setLists(response.list);
		} catch (error) {
			console.error("Error getting filtered investors list:", error);
		}
	};
	
	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				await Promise.all([
					fetchFilteredInvestorsList(),
					fetchTemplates(),
					fetchCampaigns()
				]);
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoading(false);
			}
		};
		
		fetchData();
	}, []);

	const fetchTemplates = async () => {
		try {
			const response = await getTemplates();
			if (response.status === 200) {
				setTemplates(response.templates);
			} else {
				toast.error(response.message || "Failed to fetch templates");
			}
		} catch (error) {
			console.error("Error fetching templates:", error);
			toast.error("An error occurred while fetching templates");
		}
	};
	
	useEffect(() => {
		fetchTemplates();
	}, []);

	const fetchCampaigns = async () => {
		try {
			const response = await getCampaigns();
			if (response.status === 200) {
				setCampaigns(response.campaigns);
			} else {
				toast.error(response.message || "Failed to fetch campaigns");
			}
		} catch (error) {
			console.error("Error fetching campaigns:", error);
			toast.error("An error occurred while fetching campaigns");
		}
	};

	useEffect(() => {
		fetchCampaigns();
	}, []);

	const handleOpenPopup = () => {
		setTemplateToEdit(null); // Clear any previous template data
		setOpenCreateTemplate(true);
	};

	const handleClosePopup = () => {
		setTemplateToEdit(null);
		setOpenCreateTemplate(false);
	};

	const handleSaveTemplate = async (templateData) => {
		const response = await createTemplate(templateData);

		if (response.status === 200) {
			toast.success(response.message);
			setTemplates([...templates, templateData]);
		} else {
			toast.error(response.message);
		}
	};

	const handleEditTemplate = (template) => {
		setTemplateToEdit(template);
		setOpenCreateTemplate(true);
	};

	const handleUpdateTemplate = async (templateId, templateData) => {
		const response = await updateTemplate(templateId, templateData);

		if (response.status === 200) {
			toast.success(response.message);
			setTemplates(
				templates.map((template) =>
					template._id === templateId
						? { ...template, ...templateData }
						: template
				)
			);
		} else {
			toast.error(response.message);
		}
	};

	const handleDeleteTemplate = async (templateId) => {
		const response = await deleteTemplate(templateId);
		if (response.status !== 200) {
			toast.error(response.message);
			return;
		}
		setTemplates(templates.filter((template) => template._id !== templateId));
		toast.success(response.message);
	};

	const handleListSelection = (listId) => {
		if (selectedLists.includes(listId)) {
			setSelectedLists(selectedLists.filter(id => id !== listId));
		} else {
			setSelectedLists([...selectedLists, listId]);
		}
	};

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			setSelectedLists(lists.map(list => list._id));
		} else {
			setSelectedLists([]);
		}
	};

	const handleProceedToTemplates = () => {
		if (selectedLists.length === 0) {
			toast.error("Please select at least one list to proceed");
			return;
		}
		
		setActiveTab("Template");
	};

	const handleOpenCreateList = () => {
		setListToEdit(null);
		setOpenCreateList(true);
	};

	const handleCloseCreateList = () => {
		setListToEdit(null);
		setOpenCreateList(false);
	};

	const handleSaveList = async (listData) => {
		try {
			const response = await createFilteredInvestorsList(listData);
			if (response.status === 200) {
				toast.success("List created successfully");
				// Refresh lists
				const updatedLists = await getFilteredInvestorsList();
				setLists(updatedLists.list);
				handleCloseCreateList();
			} else {
				toast.error(response.message || "Failed to create list");
			}
		} catch (error) {
			console.error("Error creating list:", error);
			toast.error("An error occurred while creating the list");
		}
	};

	const handleEditList = (list) => {
		setListToEdit(list);
		setOpenCreateList(true);
	};

	const handleUpdateList = async (listId, listData) => {
		try {
			const response = await updateFilteredInvestorsList(listId, listData);
			if (response.status === 200) {
				toast.success("List updated successfully");
				// Refresh lists
				const updatedLists = await getFilteredInvestorsList();
				setLists(updatedLists.list);
				handleCloseCreateList();
			} else {
				toast.error(response.message || "Failed to update list");
			}
		} catch (error) {
			console.error("Error updating list:", error);
			toast.error("An error occurred while updating the list");
		}
	};

	const handleDeleteConfirmation = (list) => {
		setListToDelete(list);
		setShowDeleteConfirm(true);
	};

	const handleDeleteList = async () => {
		if (!listToDelete) return;
		
		try {
			const response = await deleteFilteredInvestorsList(listToDelete._id);
			if (response.status === 200) {
				toast.success("List deleted successfully");
				// Refresh lists
				const updatedLists = await getFilteredInvestorsList();
				setLists(updatedLists.list);
			} else {
				toast.error(response.message || "Failed to delete list");
			}
		} catch (error) {
			console.error("Error deleting list:", error);
			toast.error("An error occurred while deleting the list");
		} finally {
			setShowDeleteConfirm(false);
			setListToDelete(null);
		}
	};

	const handleViewList = (listId) => {
		navigate(`/campaign/list/${listId}`);
	};

	const handleSelectTemplate = (templateId) => {
		setSelectedTemplate(templateId);
	};

	const handleProceedToCampaign = () => {
		if (!selectedTemplate) {
			toast.error("Please select a template to proceed");
			return;
		}
		
		if (selectedLists.length === 0) {
			toast.error("Please select at least one investor list");
			setActiveTab("List");
			return;
		}

		// if(user.isSubscribed === false){
		// 	toast.error("Please subscribe to create a campaign");
		// 	setSubscriptionPopup(true);
		// 	return;
		// }
		
		// Navigate to create campaign page with selected lists and template
		navigate("/campaign/create", {
			state: {
				selectedLists,
				selectedTemplate
			}
		});
	};

	const handleViewCampaign = (campaignId) => {
		navigate(`/campaign/detail/${campaignId}`);
	};

	const handleDeleteCampaign = async (campaignId) => {
		try {
			const response = await deleteCampaign(campaignId);
			if (response.status === 200) {
				toast.success("Campaign deleted successfully");
				// Refresh campaigns list
				fetchCampaigns();
			} else {
				toast.error(response.message || "Failed to delete campaign");
			}
		} catch (error) {
			console.error("Error deleting campaign:", error);
			toast.error("An error occurred while deleting the campaign");
		}
	};

	const handleRunCampaign = async (campaignId) => {
		try {

			if(user.subscriptionType !== "Pro"){
				toast.error("Please subscribe to Pro plan to create a campaign");
				setSubscriptionPopup(true);
				return;
			}

			const response = await runCampaign(campaignId);
			if (response.status === 200) {
				toast.success("Campaign started successfully");
				// Refresh campaigns list
				fetchCampaigns();
			} else {
				toast.error(response.message || "Failed to start campaign");
			}
		} catch (error) {
			console.error("Error running campaign:", error);
			toast.error("An error occurred while starting the campaign");
		}
	};

	const handleOpenScheduleModal = (campaignId) => {
		// Set default time to 30 minutes from now
		const defaultTime = new Date();
		defaultTime.setMinutes(defaultTime.getMinutes() + 30);
		
		setScheduledDate(defaultTime);
		setScheduledTime(defaultTime);
		setCampaignToSchedule(campaignId);
		setShowScheduleModal(true);
	};

	const handleCloseScheduleModal = () => {
		setShowScheduleModal(false);
		setCampaignToSchedule(null);
	};

	const handleScheduleCampaign = async (campaignId) => {
		if(user.subscriptionType !== "Pro"){
			toast.error("Please subscribe to Pro plan to create a campaign");
			setSubscriptionPopup(true);
			return;
		}
		// Open the schedule modal instead of immediately scheduling
		handleOpenScheduleModal(campaignId);
	};

	const handleConfirmSchedule = async () => {
		if (!campaignToSchedule) return;
		
		try {
			setIsScheduling(true);
			
			// Combine date and time
			const scheduledDateTime = new Date(scheduledDate);
			scheduledDateTime.setHours(
				scheduledTime.getHours(),
				scheduledTime.getMinutes(),
				0
			);
			
			// Ensure the scheduled time is in the future
			const now = new Date();
			if (scheduledDateTime <= now) {
				toast.error("Please select a future date and time");
				setIsScheduling(false);
				return;
			}

			const response = await scheduleCampaign(campaignToSchedule, scheduledDateTime.toISOString());
			if (response.status === 200) {
				toast.success("Campaign scheduled successfully");
				fetchCampaigns();
				handleCloseScheduleModal();
			} else {
				toast.error(response.message || "Failed to schedule campaign");
			}
		} catch (error) {
			console.error("Error scheduling campaign:", error);
			toast.error("An error occurred while scheduling the campaign");
		} finally {
			setIsScheduling(false);
		}
	};

	const handleCancelSchedule = async (campaignId) => {
		try {
			const response = await scheduleCampaign(campaignId, null);
			if (response.status === 200) {
				toast.success("Campaign schedule cancelled");
				// Refresh campaigns list
				fetchCampaigns();
			} else {
				toast.error(response.message || "Failed to cancel schedule");
			}
		} catch (error) {
			console.error("Error cancelling schedule:", error);
			toast.error("An error occurred while cancelling the schedule");
		}
	};

	const handleDuplicateCampaign = async (campaignId) => {
		try {
			// Get the campaign to duplicate
			const response = await getCampaignById(campaignId);
			if (response.status === 200) {
				const campaign = response.campaign;
				
				// Create a new campaign with the same data but a different name
				const newCampaignData = {
					campaignName: `${campaign.campaignName} (Copy)`,
					investorLists: campaign.investorLists.map(list => list._id),
					template: campaign.template._id,
					emailSubject: campaign.template.emailSubject,
					emailBody: campaign.template.emailBody
				};
				
				const createResponse = await createCampaign(newCampaignData);
				if (createResponse.status === 201) {
					toast.success("Campaign duplicated successfully");
					// Refresh campaigns
					const updatedResponse = await getCampaigns();
					setCampaigns(updatedResponse.campaigns);
				} else {
					toast.error(createResponse.message || "Failed to duplicate campaign");
				}
			} else {
				toast.error(response.message || "Failed to fetch campaign details");
			}
		} catch (error) {
			console.error("Error duplicating campaign:", error);
			toast.error("An error occurred while duplicating the campaign");
		}
	};

	const renderListSkeletons = () => (
		<>
			{[1, 2, 3].map((i) => (
				<tr key={i}>
					<td><div className="skeleton" style={{ width: 20, height: 20 }}/></td>
					<td><div className="skeleton" style={{ width: 150, height: 20 }}/></td>
					<td><div className="skeleton" style={{ width: 50, height: 20 }}/></td>
					<td><div className="skeleton" style={{ width: 100, height: 20 }}/></td>
					<td><div className="skeleton" style={{ width: 80, height: 20 }}/></td>
					<td><div className="skeleton" style={{ width: 120, height: 20 }}/></td>
				</tr>
			))}
		</>
	);

	const renderTemplateSkeletons = () => (
		<div className="templates-grid">
			{[1, 2, 3].map((i) => (
				<div key={i} className="template-skeleton">
					<div className="skeleton" style={{ width: "60%", height: 24, marginBottom: 12 }}/>
					<div className="skeleton" style={{ width: "80%", height: 18, marginBottom: 8 }}/>
					<div className="skeleton" style={{ width: "40%", height: 18 }}/>
					<div className="skeleton-actions">
						<div className="skeleton" style={{ width: 80, height: 32 }}/>
						<div className="skeleton" style={{ width: 32, height: 32 }}/>
					</div>
				</div>
			))}
		</div>
	);

	const renderCampaignSkeletons = () => (
		<>
			{[1, 2, 3].map((i) => (
				<tr key={i}>
					<td><div className="skeleton" style={{ width: 80, height: 24, borderRadius: 12 }}/></td>
					<td><div className="skeleton" style={{ width: 150, height: 20 }}/></td>
					<td><div className="skeleton" style={{ width: 30, height: 20 }}/></td>
					<td><div className="skeleton" style={{ width: 50, height: 20 }}/></td>
					<td><div className="skeleton" style={{ width: 60, height: 20 }}/></td>
					<td><div className="skeleton" style={{ width: 60, height: 20 }}/></td>
					<td>
						<div className="skeleton-actions">
							<div className="skeleton" style={{ width: 32, height: 32, borderRadius: '50%' }}/>
							<div className="skeleton" style={{ width: 32, height: 32, borderRadius: '50%' }}/>
							<div className="skeleton" style={{ width: 32, height: 32, borderRadius: '50%' }}/>
						</div>
					</td>
				</tr>
			))}
		</>
	);

	const renderListTab = () => (
		<div className={"listTab"}>
			<div className={"listActions"}>
				<div className="selectAllContainer">
					<input
						type="checkbox"
						id="selectAll"
						checked={selectedLists.length === lists.length && lists.length > 0}
						onChange={handleSelectAll}
						disabled={loading}
					/>
					<label htmlFor="selectAll">Select All</label>
				</div>
				<div className="actionButtons">
					<button
						className="createListBtn"
						onClick={handleOpenCreateList}
						disabled={loading}
					>
						Create New List
					</button>
					{selectedLists.length > 0 && (
						<button
							className="proceedBtn"
							onClick={handleProceedToTemplates}
							disabled={loading}
						>
							Proceed with Selected ({selectedLists.length})
						</button>
					)}
				</div>
			</div>
			<table className={"listsTable"}>
				<thead>
					<tr>
						<th>Select</th>
						<th>List Name</th>
						<th>Investors</th>
						<th>VCs</th>
						<th>Created By</th>
						<th>Sharing</th>
						<th>Actions</th>
					</tr>
				</thead>
				<tbody>
					{loading ? (
						renderListSkeletons()
					) : lists.length === 0 ? (
						<tr>
							<td colSpan="6" className="no-data">
								No lists found. Create your first list!
							</td>
						</tr>
					) : (
						lists.map((list) => (
							<tr key={list._id}>
								<td>
									<input
										type="checkbox"
										checked={selectedLists.includes(list._id)}
										onChange={() => handleListSelection(list._id)}
									/>
								</td>
								<td>{list.name}</td>
								<td>{list.investors?.length || 0}</td>
								<td>{list.vcs?.length || 0}</td>
								<td>
									{list.createdBy?._id === user?._id
										? "You"
										: `${list.createdBy?.firstName} ${list.createdBy?.lastName}`}
								</td>
								<td> - </td>
								<td className="listItemActions">
									<button
										className="viewBtn"
										onClick={() => handleViewList(list._id)}
									>
										<FiEye />
									</button>
									{list.createdBy?._id === user?._id && (
										<>
											<button
												className="editBtn"
												onClick={() => handleEditList(list)}
											>
												<FiEdit />
											</button>
											<button
												className="deleteBtn"
												onClick={() => handleDeleteConfirmation(list)}
											>
												<FaRegTrashCan />
											</button>
										</>
									)}
								</td>
							</tr>
						))
					)}
				</tbody>
			</table>

			{/* Delete Confirmation Modal */}
			{showDeleteConfirm && (
				<div className="deleteConfirmModal">
					<div className="deleteConfirmContent">
						<h3>Confirm Delete</h3>
						<p>Are you sure you want to delete the list "{listToDelete?.name}"?</p>
						<p>This action cannot be undone.</p>
						<div className="deleteConfirmActions">
							<button className="cancelBtn" onClick={() => setShowDeleteConfirm(false)}>
								Cancel
							</button>
							<button className="confirmDeleteBtn" onClick={handleDeleteList}>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);

	const renderTemplateTab = () => (
		<div className="templateTab">
			<div className="templatesHeader">
				<h2>Email Templates</h2>
				<div className="templateActions">
					{selectedTemplate && (
						<button
							className="proceedTemplateBtn"
							onClick={handleProceedToCampaign}
							disabled={loading}
						>
							Proceed with Selected Template
						</button>
					)}
					<button
						className="createTemplateBtn"
						onClick={handleOpenPopup}
						disabled={loading}
					>
						Create New Template
					</button>
				</div>
			</div>

			<div className="templatesList">
				{loading ? (
					renderTemplateSkeletons()
				) : templates.length === 0 ? (
					<div className="empty-state">
						<div className="empty-icon">📝</div>
						<h3 className="empty-title">No templates yet</h3>
						<p className="empty-description">Create your first email template to get started with campaigns</p>
					</div>
				) : (
					templates.map((template) => (
						<div 
							key={template._id} 
							className={`templateCard ${selectedTemplate === template._id ? 'selected' : ''}`}
							onClick={() => handleSelectTemplate(template._id)}
						>
							<div className="templateInfo">
								<h3>{template.templateName}</h3>
								<p>Subject: {template.emailSubject}</p>
								<p>Created by: {template.userId === user?._id ? "You" : "Capital Hub"}</p>
							</div>
							<div className="templateActions">
								<button
									className={`useBtn ${selectedTemplate === template._id ? 'selected' : ''}`}
									onClick={(e) => {
										e.stopPropagation();
										handleSelectTemplate(template._id);
									}}
								>
									{selectedTemplate === template._id ? "Selected" : "Select"}
								</button>
								<div className="iconActions">
									{template.userId === user?._id && (
										<>
											<button
												className="editBtn"
												onClick={(e) => {
													e.stopPropagation();
													handleEditTemplate(template);
												}}
											>
												<FiEdit />
											</button>
											<button
												className="deleteBtn"
												onClick={(e) => {
													e.stopPropagation();
													handleDeleteTemplate(template._id);
												}}
											>
												<FaRegTrashCan />
											</button>
										</>
									)}
								</div>
							</div>
						</div>
					))
				)}
			</div>
		</div>
	);

	const renderCampaignActions = (campaign) => {
		const { campaignStatus, _id } = campaign;
		
		return (
			<div className="campaignActions">
				<button 
					className="viewBtn" 
					onClick={() => handleViewCampaign(_id)}
					title="View Campaign"
				>
					<FiEye />
				</button>
				
				{campaignStatus === 'draft' && (
					<>
						{/* <button 
							className="editBtn" 
							onClick={() => navigate(`/campaign/edit/${_id}`)}
							title="Edit Campaign"
						>
							<FiEdit />
						</button> */}
						<button 
							className="runBtn" 
							onClick={() => handleRunCampaign(_id)}
							title="Run Campaign Now"
						>
							<FiPlay />
						</button>
						<button 
							className="scheduleBtn" 
							onClick={() => handleScheduleCampaign(_id)}
							title="Schedule Campaign"
						>
							<FiClock />
						</button>
					</>
				)}
				
				{campaignStatus === 'scheduled' && (
					<>
						<button 
							className="cancelBtn" 
							onClick={() => handleCancelSchedule(_id)}
							title="Cancel Schedule"
						>
							<FiX />
						</button>
						<button 
							className="runBtn" 
							onClick={() => handleRunCampaign(_id)}
							title="Run Campaign Now"
						>
							<FiPlay />
						</button>
					</>
				)}
				
				<button 
					className="deleteBtn" 
					onClick={() => handleDeleteCampaign(_id)}
					title="Delete Campaign"
				>
					<FaRegTrashCan />
				</button>
				
				<button 
					className="copyBtn" 
					onClick={() => handleDuplicateCampaign(_id)}
					title="Duplicate Campaign"
				>
					<IoCopyOutline />
				</button>
			</div>
		);
	};

	const renderOutreachTab = () => (
		<div className={"outreachTab"}>
			<div className={"outreachHeader"}>
				<h2>Your Email Campaigns</h2>
				<div className={"outreachActions"}>
					<input 
						type="text" 
						placeholder="Search" 
						className={"searchInput"}
						disabled={loading}
					/>
					<button
						className={"createCampaignBtn"}
						onClick={() => {
							if (lists.length === 0) {
								toast.error("Please create at least one investor list first");
								setActiveTab("List");
								return;
							}
							if (templates.length === 0) {
								toast.error("Please create at least one email template first");
								setActiveTab("Template");
								return;
							}
							setActiveTab("List");
						}}
						disabled={loading}
					>
						+ Create new campaign
					</button>
				</div>
			</div>
			<table className={"campaignsTable"}>
				<thead>
					<tr>
						<th>Status</th>
						<th>Email campaign name</th>
						<th>Emails</th>
						<th>Recipients</th>
						<th>Open rate</th>
						<th>Reply rate</th>
						<th>Action</th>
					</tr>
				</thead>
				<tbody>
					{loading ? (
						renderCampaignSkeletons()
					) : campaigns.length === 0 ? (
						<tr>
							<td colSpan="7" className="no-campaigns">
								No campaigns found. Create your first campaign!
							</td>
						</tr>
					) : (
						campaigns.map((campaign) => (
							<tr key={campaign._id}>
								<td>
									<span className={`status-badge ${campaign.campaignStatus}`}>
										{campaign.campaignStatus.charAt(0).toUpperCase() + campaign.campaignStatus.slice(1)}
									</span>
								</td>
								<td>{campaign.campaignName}</td>
								<td className="emailIcon-td">
									<MdOutlineMail size={20} className="emailIcon" />
								</td>
								<td>{campaign.investors?.length || 0}</td>
								<td>
									{campaign.metrics?.totalSent > 0
										? `${Math.round((campaign.metrics.totalOpened / campaign.metrics.totalSent) * 100)}%`
										: "0%"}
								</td>
								<td>
									{campaign.metrics?.totalSent > 0
										? `${Math.round((campaign.metrics.totalReplied / campaign.metrics.totalSent) * 100)}%`
										: "0%"}
								</td>
								<td>
									{renderCampaignActions(campaign)}
								</td>
							</tr>
						))
					)}
				</tbody>
			</table>
		</div>
	);

	return (
		<>
			<div className={`campaignManagement ${theme}`}>
				<div className={"tabs"}>
					<button
						className={activeTab === "List" ? "activeTab" : ""}
						onClick={() => setActiveTab("List")}
					>
						List
					</button>
					<button
						className={activeTab === "Template" ? "activeTab" : ""}
						onClick={() => setActiveTab("Template")}
					>
						Template
					</button>
					<button
						className={activeTab === "Outreach" ? "activeTab" : ""}
						onClick={() => setActiveTab("Outreach")}
					>
						Outreach
					</button>
				</div>

				<div className={"tabContent"}>
					{activeTab === "List" && renderListTab()}
					{activeTab === "Template" && renderTemplateTab()}
					{activeTab === "Outreach" && renderOutreachTab()}
				</div>
			</div>

			<CreateTemplatePopup
				isOpen={openCreateTemplate}
				onClose={handleClosePopup}
				onSave={templateToEdit ? handleUpdateTemplate : handleSaveTemplate}
				templateToEdit={templateToEdit}
			/>

			<CreateListPopup
				isOpen={openCreateList}
				onClose={handleCloseCreateList}
				onSave={listToEdit ? (data) => handleUpdateList(listToEdit._id, data) : handleSaveList}
				listToEdit={listToEdit}
			/>

			{/* Schedule Modal */}
			<Modal
				isOpen={showScheduleModal}
				onRequestClose={handleCloseScheduleModal}
				contentLabel="Schedule Campaign"
				className="schedule-modal"
				overlayClassName="schedule-modal-overlay"
			>
				<div className="schedule-modal-content">
					<h2>Schedule Campaign</h2>
					<p>Select when you want to send this campaign:</p>
					
					<div className="schedule-form">
						<div className="form-group">
							<label>Date:</label>
							<DatePicker
								selected={scheduledDate}
								onChange={date => setScheduledDate(date)}
								minDate={new Date()}
								dateFormat="MMMM d, yyyy"
								className="date-picker"
							/>
						</div>
						
						<div className="form-group">
							<label>Time:</label>
							<DatePicker
								selected={scheduledTime}
								onChange={time => setScheduledTime(time)}
								showTimeSelect
								showTimeSelectOnly
								timeIntervals={15}
								timeCaption="Time"
								dateFormat="h:mm aa"
								className="time-picker"
							/>
						</div>
					</div>
					
					<div className="schedule-actions">
						<button 
							className="cancel-button" 
							onClick={handleCloseScheduleModal}
							disabled={isScheduling}
						>
							Cancel
						</button>
						<button 
							className="confirm-button" 
							onClick={handleConfirmSchedule}
							disabled={isScheduling}
						>
							{isScheduling ? 'Scheduling...' : 'Schedule Campaign'}
						</button>
					</div>
				</div>
			</Modal>
			<SubscriptionPopup
				isOpen={subscriptionPopup}
				onClose={() => setSubscriptionPopup(false)}
				fromCampaign={true}
			/>
		</>
	);
};

export default CampaignManagement;
